import { useEffect, useRef, useState } from "react";

const TONES = {
  blue: { tile: "bg-primary/10 text-primary ring-primary/20", num: "text-foreground", bar: "from-primary to-cyan-400" },
  emerald: { tile: "bg-emerald-500/10 text-emerald-600 ring-emerald-500/20", num: "text-emerald-600", bar: "from-emerald-500 to-teal-300" },
  orange: { tile: "bg-orange-500/10 text-orange-600 ring-orange-500/20", num: "text-orange-600", bar: "from-orange-500 to-amber-300" },
  cyan: { tile: "bg-cyan-500/10 text-cyan-600 ring-cyan-500/20", num: "text-cyan-600", bar: "from-cyan-500 to-sky-300" },
  amber: { tile: "bg-amber-500/10 text-amber-600 ring-amber-500/20", num: "text-amber-600", bar: "from-amber-500 to-yellow-300" },
  rose: { tile: "bg-rose-500/10 text-rose-600 ring-rose-500/20", num: "text-rose-600", bar: "from-rose-500 to-pink-300" },
  violet: { tile: "bg-violet-500/10 text-violet-600 ring-violet-500/20", num: "text-violet-600", bar: "from-violet-500 to-fuchsia-300" },
  slate: { tile: "bg-slate-500/10 text-slate-600 ring-slate-500/20", num: "text-foreground", bar: "from-slate-500 to-slate-300" },
};

const reduced = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export function useCountUp(target, duration = 900) {
  const n = Number(target);
  const [v, setV] = useState(() => (reduced() ? n : 0));
  const prev = useRef(0);
  useEffect(() => {
    if (!Number.isFinite(n)) return undefined;
    if (reduced()) { setV(n); prev.current = n; return undefined; }
    const from = prev.current;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const e = 1 - Math.pow(1 - p, 3);
      setV(from + (n - from) * e);
      if (p < 1) raf = requestAnimationFrame(tick); else prev.current = n;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [n, duration]);
  return v;
}

export function CountUp({ value, prefix = "", suffix = "", decimals = 0 }) {
  const v = useCountUp(value);
  if (!Number.isFinite(Number(value))) return <>{value ?? "—"}</>;
  return <>{prefix}{Number(v).toLocaleString("en-IN", { maximumFractionDigits: decimals, minimumFractionDigits: 0 })}{suffix}</>;
}

export default function StatCard({ label, value, icon: Icon, tone = "blue", hint, delay = 0, testId, prefix = "", suffix = "", decimals = 0, onClick, active = false }) {
  const t = TONES[tone] || TONES.blue;
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      ref={ref}
      onMouseMove={onMove}
      onClick={onClick}
      type={onClick ? "button" : undefined}
      data-testid={testId}
      style={{ "--i": delay }}
      className={`panel spotlight hover-lift fade-up group relative overflow-hidden p-4 sm:p-5 text-left w-full ${active ? "ring-2 ring-primary/40 border-primary/40" : ""} ${onClick ? "cursor-pointer" : ""}`}
    >
      <div className={`absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r ${t.bar} opacity-0 group-hover:opacity-100 transition-opacity`} />
      <div className="flex items-start justify-between gap-3">
        <div className="text-[11px] font-mono-label font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
        {Icon && (
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ring-1 ${t.tile} transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3`}>
            <Icon className="h-4 w-4" />
          </span>
        )}
      </div>
      <div className={`font-display text-3xl sm:text-[34px] font-extrabold tracking-tight mt-2 ${t.num}`} data-testid={testId ? `${testId}-value` : undefined}>
        <CountUp value={value} prefix={prefix} suffix={suffix} decimals={decimals} />
      </div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </Comp>
  );
}
